<script setup>

import Button from "primevue/button";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";
import Popover from 'primevue/popover';
import InputNumber from "primevue/inputnumber";
import Avatar from 'primevue/avatar';
import InputText from "primevue/inputtext";
import ToggleSwitch from "primevue/toggleswitch";

import {computed, onMounted, ref} from "vue";
import ReverseCheckbox from "./ReverseCheckbox.vue";
import {uniq} from "lodash";
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    init: Boolean,
    worker: Boolean,
    workers: Array,
    editable: Boolean,
})

const selectedWorker = defineModel('selectedWorker')
const op = ref()
const interval = ref()
const currentTimeSlot = ref()
const toggleWorkers = (event, timeSlot) => {
    currentTimeSlot.value = timeSlot;
    op.value.toggle(event)
}

const toggleInterval = (event, timeSlot) => {
    currentTimeSlot.value = timeSlot;
    interval.value.toggle(event)
}

const daySlots = defineModel();
const deleted = defineModel('deleted');

const currentTimeslot = ref();
const currentDay = ref();
const currentDayslot = ref();
const duplicate = (event, timeslot, day, dayslot) => {
    currentDay.value = day;
    currentDayslot.value = dayslot;
    currentTimeslot.value = timeslot;
    daysPopover.value.toggle(event);
}

const addVacationSlot = () => {
    const daysTemplate = {
        valid: {
            from: null,
            to: null
        },
        name: '',
        showName: true,
        type: 'vacation',
        exclude_workers: []
    }

    daySlots.value.push(daysTemplate);
}

const addDaySlot = () => {
    const daysTemplate = {
        valid: {
            from: null,
            to: null
        },
        type: 'regular',
        days: {
            mon: { id: 'mon', label: trans('common.dow_long.mon'), selected: false, timeslots: [] },
            tue: { id: 'tue', label: trans('common.dow_long.tue'), selected: false, timeslots: [] },
            wed: { id: 'wed', label: trans('common.dow_long.wed'), selected: false, timeslots: [] },
            thu: { id: 'thu', label: trans('common.dow_long.thu'), selected: false, timeslots: [] },
            fri: { id: 'fri', label: trans('common.dow_long.fri'), selected: false, timeslots: [] },
            sat: { id: 'sat', label: trans('common.dow_long.sat'), selected: false, timeslots: [] },
            sun: { id: 'sun', label: trans('common.dow_long.sun'), selected: false, timeslots: [] },
        }
    }
    daySlots.value.push(daysTemplate);
}

const addTimeSlot = (container, blocking = true) => {
    container.push({
        time_start: null,
        time_end: null,
        blocking: blocking,
        interval: 30,
        pause: 30,
        exclude_workers: [],
        id_reservation_workers: selectedWorker.value
    });
}

onMounted(() => {
    if (props.init && daySlots.value.length === 0) addDaySlot()
})

const timeToMinutes = (time) => {
    if (!time) {
        throw new Error("Invalid time provided");
    }
    const [hours, minutes] = time?.split(':').map(Number);
    return hours * 60 + minutes;
}

const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
    const mins = (minutes % 60).toString().padStart(2, '0');
    return `${hours}:${mins}`;
}

const getValidStartTimes = (timeslots, excludeSlot = null, currentSlot) => {
    const validStartTimes = [];

    // Pokud existuje timeslot, který chceme vyloučit, odstraníme ho dočasně ze seznamu
    const filteredTimeslots = excludeSlot ? timeslots.filter(slot => slot !== excludeSlot) : timeslots;

    const dayStart = 0; // 00:00
    const dayEnd = 24 * 60; // 24:00

    for (let time = dayStart; time < dayEnd; time += 30) {
        const currentTime = minutesToTime(time);

        // Zkontrolujeme, zda je časový slot platný
        const isValid = filteredTimeslots.length === 0 ? true : !filteredTimeslots.some(slot => {
            if (slot === currentSlot) {
                return false; // Aktuální slot se ignoruje
            }
            if (!slot.time_start || !slot.time_end) {
                return false; // Slot s null se ignoruje
            }
            const slotStart = timeToMinutes(slot.time_start);
            const slotEnd = timeToMinutes(slot.time_end);
            return time >= slotStart && time < slotEnd;
        });

        if (isValid) {
            validStartTimes.push({
                value: currentTime,
                label: currentTime
            });
        }
    }

    return validStartTimes;
}

const disabledPlanAddition = computed(() => {
    return !(daySlots.value.length === 1 || !(!daySlots.value[daySlots.value.length - 1]?.valid?.from || !daySlots.value[daySlots.value.length - 1]?.valid?.to));
})

const getValidEndTimes = (timeslots, startTime, excludeSlot = null, currentSlot) => {
    const validEndTimes = [];

    // Pokud existuje timeslot, který chceme vyloučit, odstraníme ho dočasně ze seznamu
    const filteredTimeslots = excludeSlot ? timeslots.filter(slot => slot !== excludeSlot) : timeslots;

    const startMinutes = timeToMinutes(startTime);
    const dayEnd = 24 * 60; // 24:00

    for (let time = startMinutes + 30; time <= dayEnd; time += 30) {
        const endTime = minutesToTime(time);
        const isValid = filteredTimeslots.length === 0 ? true : !filteredTimeslots.some(slot => {
            if (slot === currentSlot) {
                return false; // Aktuální slot se ignoruje
            }
            if (!slot.time_start || !slot.time_end) {
                return false; // Slot s null se ignoruje
            }
            const slotStart = timeToMinutes(slot.time_start);
            const slotEnd = timeToMinutes(slot.time_end);
            return (time > slotStart && startMinutes < slotEnd);
        });

        if (isValid) {
            const diffInMinutes = time - startMinutes;
            const diffInHours = `${Math.floor(diffInMinutes / 60)}:${(diffInMinutes % 60).toString().padStart(2, '0')}`;

            validEndTimes.push({
                value: endTime,
                label: `${endTime} (${diffInHours} h)`
            });
        }
    }

    return validEndTimes;
}
const daysPopover = ref();
const days = ['mon','tue','wed','thu','fri','sat','sun'].map(k => ({
    name: trans(`common.dow_long.${k}`),
    identifier: k,
}))

const workersCustomPlan = (plan) => {
    if (!plan || plan.type === 'vacation')
        return [];
    const usedWorkers = uniq(
        Object.values(plan?.days)
            .flatMap(day => day.timeslots ?? [])
            .flatMap(slot => slot.id_reservation_workers ?? [])
    ) ?? [];


    const excludedWorkers = props.workers.filter(worker =>
        usedWorkers.includes(worker.id)
    );

    const includedWorkers = props.workers.filter(worker =>
        !usedWorkers.includes(worker.id)
    );

    const workersCustomPlan = [];

    if (excludedWorkers.length)
    {
        workersCustomPlan.push({
            label: trans('forms.timeslot_workers_custom'),
            hasCustom: true,
            items: excludedWorkers
        },)
    }

    if (includedWorkers.length)
    {
        workersCustomPlan.push({
            label: trans('forms.timeslot_workers_default'),
            hasCustom: true,
            items: includedWorkers
        },)
    }

    return workersCustomPlan;
}

const filterByWorker = (container) => {
    return container.filter(timeSlot => timeSlot.id_reservation_workers === selectedWorker.value)
}

const isLast = (array, index) => {
    const filteredIndexes = array
        .map((item, i) => filterByWorker([item]).length ? i : null)
        .filter(i => i !== null);

    return filteredIndexes[filteredIndexes.length - 1] === index;
}
</script>

<template>
    <div v-for="(daySlot, i) in daySlots" :key="`${i}-daySlot`"
         class="bg-white border shadow overflow-hidden dark:bg-transparent dark:border-gray-800 rounded-lg w-full  mb-4"
         >
        <div v-if="daySlot.type === 'regular'" class="flex gap-2 flex-col justify-evenly p-3 border-b dark:border-gray-800">
            <div v-for="day in Object.values(daySlot.days)" :key="`${i}-${day.id}`"
                 class=" w-100 gap-2 grid grid-cols-8 items-center">
                <Button :label="day.label.substring(0,2)" style="width: 30px; height: 30px"
                        :severity="filterByWorker(day.timeslots).length ? 'primary' : 'secondary'" :rounded="true"
                        :variant="filterByWorker(day.timeslots).length && !filterByWorker(day.timeslots)[0]?.blocking ? 'outlined': null"></Button>
                <template v-for="(timeSlot, y) in day.timeslots">
                    <div v-if="timeSlot.id_reservation_workers === selectedWorker"
                         class=" flex gap-2 col-span-7 col-start-2">
                        <Select v-model="timeSlot.time_start" @update:model-value="timeSlot.time_end = null"
                                :options="getValidStartTimes(day.timeslots, timeSlot, timeSlot.time_start)"
                                style="width: 120px"
                                size="small" class="text-center !bg-gray-100 dark:!bg-gray-900"
                                optionLabel="label" optionValue="value">
                            <template #value="{value}">
                                <p class="absolute">{{ value }}</p>
                            </template>
                        </Select>
                        <Select v-if="timeSlot.time_start" v-model="timeSlot.time_end" :placeholder="$t('forms.timeslot_end_placeholder')"
                                style="width: 120px"
                                size="small" class="text-center !bg-gray-100 dark:!bg-gray-900"
                                :options="getValidEndTimes(day.timeslots, timeSlot.time_start, timeSlot, timeSlot.time_end)"
                                optionLabel="label" optionValue="value">
                            <template #value="label">
                                <p class="absolute">{{ label.value }}</p>
                            </template>
                        </Select>

                        <div class="flex gap-2">
                            <Button v-if="!timeSlot.blocking" size="small" label="Interval"
                                    @click="toggleInterval($event, timeSlot)"
                                    severity="secondary" icon="pi pi-clock"></Button>
                            <Button size="small" @click="day.timeslots.splice(y, 1)" style="width: 24px"
                                    severity="danger"
                                    icon="pi pi-trash" text></Button>
                            <Button v-if="timeSlot.time_end && isLast(day.timeslots, y)" size="small" v-tooltip.bottom="$t('common.duplicate')"
                                    @click="duplicate($event, timeSlot, day, daySlot)" style="width: 24px"
                                    severity="secondary"
                                    icon="pi pi-copy" text></Button>
                            <Button v-if="false" size="small" style="width: 24px" text severity="secondary"
                                    @click="toggleWorkers($event,timeSlot)" icon="pi pi-users"></Button>
                            <Button v-if="isLast(day.timeslots, y) && timeSlot.blocking" size="small"
                                    style="width: 24px" text severity="secondary" @click="() => { day.selected = true; addTimeSlot(day.timeslots); }"
                                    icon="pi pi-plus"></Button>
                        </div>
                    </div>
                </template>

                    <Button v-if="filterByWorker(day.timeslots).length === 0" class="col-span-3 col-start-2"
                            :disabled="filterByWorker(day.timeslots).length > 0 && !filterByWorker(day.timeslots)[filterByWorker(day.timeslots).length-1].time_end"
                            :label="$t('forms.timeslot_add_time')" @click="() => { day.selected = true; addTimeSlot(day.timeslots); }" icon="pi pi-plus" text size="small"
                    ></Button>
                    <Button v-if="filterByWorker(day.timeslots).length === 0" class="col-span-4 col-start-5" text
                            :disabled="filterByWorker(day.timeslots).length > 0 && !filterByWorker(day.timeslots)[filterByWorker(day.timeslots).length-1].time_end"
                            :label="$t('forms.timeslot_add_work_hours')" @click="() => { day.selected = true; addTimeSlot(day.timeslots, false); }" icon="pi pi-plus"
                            size="small"
                    ></Button>
                    <div v-else-if="!filterByWorker(day.timeslots)[0].blocking && filterByWorker(day.timeslots).length > 0 && filterByWorker(day.timeslots)[0].time_end"
                         class="text-sm text-center opacity-75 col-start-2 col-span-7">
                        {{ $t('forms.timeslot_booking_desc', { start: filterByWorker(day.timeslots)[0].time_start, end: filterByWorker(day.timeslots)[0].time_end, interval: filterByWorker(day.timeslots)[0].interval }) }}<template v-if="filterByWorker(day.timeslots)[0].pause"> {{ $t('forms.timeslot_booking_desc_pause', { pause: filterByWorker(day.timeslots)[0].pause }) }}</template>.
                    </div>
                    <div v-else-if="!filterByWorker(day.timeslots)[0].blocking && filterByWorker(day.timeslots).length > 0"
                         class="text-sm text-center opacity-75 col-start-2 col-span-7">
                        {{ $t('forms.timeslot_fill_times') }}
                    </div>
            </div>
        </div>
        <div class="p-3 dark:bg-transparent  dark:border-gray-800"
             :class="daySlot.type === 'vacation' ? 'bg-blue-50 dark:bg-blue-950' : 'bg-neutral-50 dark:bg-neutral-950'">
            <div class=" flex gap-2 items-center">
                <div v-if="daySlot.type === 'vacation'">
                    <p class="text-lg font-bold"><i class="pi pi-directions mr-1 text-roori-blue"></i> {{ $t('forms.timeslot_vacation') }}</p>
                    <p class="text-sm opacity-70">{{ $t('forms.timeslot_vacation_desc') }}</p>
                </div>
                <div v-else-if="i > 0" class="text-lg font-bold">
                    {{ $t('forms.timeslot_limited') }}
                </div>
                <div v-else class="text-lg font-bold">
                    {{ $t('forms.timeslot_default') }}
                </div>
                <div class="grow"></div>
                <Select v-if="worker && workersCustomPlan(daySlot).length" size="small" v-model="selectedWorker" class="w-48 !rounded-full" option-value="id" option-label="name" show-clear option-group-children="items" option-group-label="label" :options="workersCustomPlan(daySlot)" dropdown-icon="pi pi-users" :placeholder="$t('forms.timeslot_colleagues_placeholder')">
                    <template #option="{ option }">
                        <div class="flex items-center gap-3">
                            <Avatar size="normal" class="!w-6 !h-6 !text-sm" :label="option.initials"></Avatar>
                            <p class="text-sm">{{option.name}}</p>
                        </div>
                    </template>
                </Select>
                <Button v-if="i > 0" severity="danger" @click="() => {
                    daySlots.splice(i, 1);
                    deleted.push({
                        valid_from: daySlot.valid.from,
                        valid_to: daySlot.valid.to,
                        type: daySlot.type,
                        id: daySlot.id,
                    })
                }" icon="pi pi-trash"></Button>

                <Button v-if="/*worker && daySlot.type === 'vacation'*/false" outlined :label="$t('forms.timeslot_colleagues_placeholder')"
                        @click="toggleWorkers($event,daySlot)" icon="pi pi-users"></Button>
                <slot v-if="(i === 0 && daySlots.length === 1) || !(daySlots.length - 1 > i)"></slot>
            </div>

            <div v-if="/*daySlot.type === 'vacation'*/false"
                 class="border-t border-b border-blue-200 -mx-3 my-2 p-3 mb-3 bg-blue-100 dark:border-blue-700 dark:bg-blue-950 flex items-center gap-3">
                <InputText v-model="daySlot.name" :placeholder="$t('forms.timeslot_vacation_name_placeholder')"></InputText>
                <ToggleSwitch v-model="daySlot.showName"></ToggleSwitch>
                <p class="grow text-blue-600 dark:text-blue-300">{{ $t('forms.timeslot_show_label') }}</p>
            </div>
            <div v-if="i > 0" class="flex items-center w-full gap-4 mt-2">
                <DatePicker v-model="daySlot.valid.from"
                            :maxDate="typeof daySlot.valid.to === 'string' ? new Date(Date.parse(daySlot.valid.to)) : daySlot.valid.to"
                            fluid
                            :placeholder="daySlot.type === 'vacation' ? $t('forms.timeslot_vacation_from') : $t('forms.timeslot_plan_from')"
                            date-format="dd.mm.yy"></DatePicker>
                <div>-</div>
                <DatePicker v-model="daySlot.valid.to"
                            :minDate="typeof daySlot.valid.from === 'string' ? new Date(Date.parse(daySlot.valid.from)) : daySlot.valid.from "
                            fluid
                            :placeholder="daySlot.type === 'vacation' ? $t('forms.timeslot_vacation_to') : $t('forms.timeslot_plan_to')"
                            date-format="dd.mm.yy"></DatePicker>
            </div>
        </div>
    </div>


    <div class="flex gap-4" :class="editable ? 'justify-center':''">
        <Button :disabled="disabledPlanAddition" severity="secondary"
                v-tooltip="disabledPlanAddition ? $t('forms.timeslot_fill_plan_first') : ''"
                icon="pi pi-plus" @click="addDaySlot" :label="$t('forms.timeslot_add_plan')"></Button>

        <Button :disabled="disabledPlanAddition" severity="secondary" class="!bg-blue-100 hover:!bg-blue-200 dark:!bg-blue-900 dark:hover:!bg-blue-800"
                v-tooltip="disabledPlanAddition ? $t('forms.timeslot_fill_plan_first') : ''"
                icon="pi pi-directions" @click="addVacationSlot" :label="$t('forms.timeslot_add_vacation')"></Button>
    </div>

    <Popover v-if="worker" ref="op">
        <div class="flex flex-col gap-2">
            <p class="text-sm font-bold">{{ $t('forms.timeslot_colleagues_in_slot') }}</p>
            <div v-for="_worker in workers" class="flex items-center gap-2 ">
                <ReverseCheckbox v-model="currentTimeSlot.exclude_workers" :value="_worker.id"></ReverseCheckbox>
                <Avatar :label="_worker.initials"></Avatar>
                <p :class="currentTimeSlot.exclude_workers?.includes(_worker.id) ? 'line-through' : ''">
                    {{ _worker.name }}</p>
            </div>
            <Button size="small" outlined severity="secondary" icon="pi pi-ban" @click="() => {
                currentTimeSlot.exclude_workers = workers.map((el) => el.id)
            }" :label="$t('forms.timeslot_remove_all')"></Button>
        </div>
    </Popover>
    <Popover ref="interval">
        <div class="p-1 w-[200px] space-y-3 text-gray-800">

            <!-- Interval -->
            <div class="flex items-center gap-2">
                <i class="pi pi-clock text-gray-500 text-lg"></i>
                <p class="w-28 text-sm font-medium">{{ $t('forms.timeslot_interval') }}</p>
                <InputNumber
                    class="w-full text-sm"
                    v-model="currentTimeSlot.interval"
                    inputId="mile"
                    min="5"
                    suffix=" min"
                    fluid
                />
            </div>

            <!-- Pauza -->
            <div class="flex items-center gap-2">
                <i class="pi pi-pause text-gray-500 text-lg"></i>
                <p class="w-28 text-sm font-medium">{{ $t('forms.timeslot_pause') }}</p>
                <InputNumber
                    class="w-full text-sm"
                    v-model="currentTimeSlot.pause"
                    inputId="pause"
                    min="0"
                    suffix=" min"
                    fluid
                />
            </div>

        </div>
    </Popover>
    <Popover ref="daysPopover">
        <div class="flex items-center gap-2 mb-3 p-2 px-3 bg-gray-100 dark:bg-gray-900 dark:border-neutral-700 -mx-3 -mt-3 rounded-t border-b">
            <i class="pi pi-copy"></i>
            <p>
                <span>{{ $t('forms.timeslot_duplicate_title') }}</span>
            </p>
        </div>
        <div class="flex gap-2">
            <Button v-for="day in days" rounded severity="secondary" :disabled="currentDay.label === day.name" @click="() => {
                if (currentDayslot && currentDayslot.days && currentDayslot.days[day.identifier]) {
                    currentDayslot.days[day.identifier].timeslots = JSON.parse(JSON.stringify(currentDay?.timeslots)) ?? [];
                    currentDayslot.days[day.identifier].selected = true
                }
            }">{{ day.name.substring(0, 2) }}
            </Button>
        </div>
    </Popover>
</template>

<style scoped lang="scss">

</style>
